// ---------------------------------------------------------------------------
// V2 judge-creation mappers — shared by the legacy `/grader-runs` service
// (`workflowApi.ts`) and the new `/grader-gens` service (`graderGenApi.ts`).
//
// When a create request is dispatched with `useLegacy: false`, WorkflowSvc
// calls the worker-py v2 pipeline (`/judges:create?api-version=2026-06-10`) and
// stores the raw `CreateJudgeV2Response` verbatim on the result's `output`
// field, tagged `workerApiVersion === V2_WORKER_API_VERSION`. The v1 typed
// fields (`variants` / `finalPrompt` / `finalAccuracy` / `finalKappa`) stay
// empty for v2 runs.
//
// The v2 response is a ranked set of candidate judges. Per the minimal-UI
// decision, we flatten each ranked candidate into the existing `JudgeVariant`
// card shape, surfacing headline metrics (accuracy / kappa / weighted-kappa /
// krippendorff-alpha / f1 / prompt). Metrics are read from each candidate's
// full-dataset evaluation (`fullDataset.metrics`) when present, falling back to
// the held-out calibration split; the remaining v2-only data (rubric, scoring
// scheme, bias audit) is not surfaced.
//
// This module also owns the v1 → v2 orchestrator-settings translation so the
// existing advanced-settings UI drives the v2 pipeline where fields overlap.
// ---------------------------------------------------------------------------

import type { JudgeOrchestratorSettings } from './grader.interface';
import type { JudgeVariant } from './graderRun.interface';

/**
 * `workerApiVersion` tag the .NET worker stamps on a v2 result. Mirrors
 * `WorkerPyApiVersion.CreateJudgeV2` in WorkflowSvc. Result mappers branch on
 * this (or on the presence of `output`) to pick the v2 flatten path.
 */
export const V2_WORKER_API_VERSION = 'createJudgeV2';

// ─── Orchestrator-settings translation (v1 UI → v2 wire) ─────────────────────

/**
 * Wire shape sent as `orchestratorSettings` when `useLegacy: false`. WorkflowSvc
 * forwards this blob to worker-py as `v2Settings` unchanged, so field names must
 * match the worker-py `V2OrchestratorSettings` camelCase contract. Only the
 * fields that overlap the existing advanced-settings UI are mapped; every other
 * v2 knob falls back to the backend default by being omitted.
 */
export interface V2OrchestratorSettings {
  numCandidates?: number;
  maxIterations?: number;
  topNForOptimization?: number;
  maxEvalExamples?: number;
}

/**
 * Translate the v1 advanced-settings UI values into the v2 wire shape, mapping
 * only the fields that have a v2 equivalent. Undefined keys are dropped so the
 * v2 pipeline applies its own defaults.
 */
export function toV2OrchestratorSettings(
  v1: JudgeOrchestratorSettings,
): V2OrchestratorSettings {
  const v2: V2OrchestratorSettings = {};
  if (v1.numVariants !== undefined) v2.numCandidates = v1.numVariants;
  if (v1.maxIterations !== undefined) v2.maxIterations = v1.maxIterations;
  if (v1.topK !== undefined) v2.topNForOptimization = v1.topK;
  if (v1.maxEvalExamples !== undefined) v2.maxEvalExamples = v1.maxEvalExamples;
  return v2;
}

// ─── Ranked-candidate → variant flatten (v2 output → existing cards) ──────────

/** Headline agreement metrics on a calibrated candidate (camelCase wire). */
interface WireV2Metrics {
  accuracy?: number;
  cohensKappa?: number;
  f1Macro?: number;
  weightedKappa?: number;
  krippendorffAlpha?: number;
}

interface WireV2Calibration {
  metrics?: WireV2Metrics;
}

interface WireV2Prompt {
  system?: string;
  userTemplate?: string;
}

interface WireV2Candidate {
  candidateId?: string;
  prompt?: WireV2Prompt;
  strategies?: string[];
}

interface WireV2RankedCandidate {
  rank?: number;
  candidate?: WireV2Candidate;
  calibration?: WireV2Calibration;
  // Metrics computed over the full labelled dataset. Preferred for display;
  // `calibration` (held-out split only) is the fallback when this is absent.
  fullDataset?: WireV2Calibration;
}

/** Subset of the raw `CreateJudgeV2Response` blob this module reads. */
export interface WireV2Output {
  candidates?: WireV2RankedCandidate[];
}

/** Normalized v2 result shared by both services' result shapes. */
export interface MappedV2Result {
  variants: JudgeVariant[];
  recommendedVariantId?: string;
  finalAccuracy?: number;
  finalKappa?: number;
  /** Top-ranked candidate's system prompt (the provisioned judge's prompt). */
  finalPrompt?: string;
  /** Top-ranked candidate's strategy mix, surfaced as the winning technique. */
  winningTechnique?: string;
}

function candidateTechnique(c: WireV2RankedCandidate): string {
  const strategies = c.candidate?.strategies;
  if (strategies && strategies.length > 0) return strategies.join('+');
  return c.rank !== undefined ? `Rank ${c.rank}` : 'candidate';
}

// Prefer the full-dataset evaluation metrics; fall back to the held-out
// calibration split when the pipeline didn't emit a `fullDataset` block.
function candidateMetrics(c: WireV2RankedCandidate): WireV2Metrics {
  return c.fullDataset?.metrics ?? c.calibration?.metrics ?? {};
}

function mapCandidate(c: WireV2RankedCandidate): JudgeVariant {
  const metrics = candidateMetrics(c);
  return {
    name: c.candidate?.candidateId ?? candidateTechnique(c),
    technique: candidateTechnique(c),
    cohensKappa: metrics.cohensKappa ?? 0,
    accuracy: metrics.accuracy ?? 0,
    // No v2 equivalent for one-off accuracy / precision / recall / bias.
    oneOffAccuracy: 0,
    weightedKappa: metrics.weightedKappa ?? 0,
    krippendorffAlpha: metrics.krippendorffAlpha ?? 0,
    bias: 0,
    f1Score: metrics.f1Macro ?? 0,
    precision: 0,
    recall: 0,
    judgeOutput: {
      systemPrompt: c.candidate?.prompt?.system,
      userTemplate: c.candidate?.prompt?.userTemplate,
    },
  };
}

/**
 * Flatten a raw v2 `output` blob into the existing variant-card result shape.
 * Candidates are assumed pre-ranked by the pipeline (rank 1 first); the
 * rank-1 candidate becomes the recommended variant and headline metrics.
 * Metrics are taken from each candidate's full-dataset evaluation when present.
 */
export function mapV2Output(output: WireV2Output): MappedV2Result {
  const ranked = output.candidates ?? [];
  const variants = ranked.map(mapCandidate);
  const top = ranked[0];
  const topMetrics = top ? candidateMetrics(top) : {};
  return {
    variants,
    recommendedVariantId: top?.candidate?.candidateId,
    finalAccuracy: topMetrics.accuracy,
    finalKappa: topMetrics.cohensKappa,
    finalPrompt: top?.candidate?.prompt?.system,
    winningTechnique: top ? candidateTechnique(top) : undefined,
  };
}
